import {RecipeGraph, RecipeNode} from '../api/index.js'
import type {IngredientRecipeSubgraphNode} from '../api/types.js'
import {DisplayNode, Link, assignLinkShape} from './types.js'
import {NODE_RADIUS, NODE_SPACING, LEVEL_HEIGHT} from './dimensions.js'

export class DisplayGraph {
  rootNodeIds: string[];
  readonly nodes: DisplayNode[];
  readonly links: Link<DisplayNode>[] = [];
  nodesById: Map<string, DisplayNode>;
  private width: number;
  private height: number;

  // Store callbacks for hidden state management
  private hideNodeFn?: (nodeId: string) => void;
  private showNodeFn?: (nodeId: string) => void;
  private isNodeHiddenFn?: (nodeId: string) => boolean;
  // Store callbacks for fold state management
  private isNodeExpandedFn?: (nodeId: string) => boolean;
  private collapseNodeFn?: (nodeId: string) => void;

  // Mappings
  private childrenMap: Map<string, DisplayNode[]>;
  private descendantsMap: Map<string, DisplayNode[]>;
  private parentsMap: Map<string, DisplayNode[]>;
  private ancestorsMap: Map<string, DisplayNode[]>;

  constructor(recipeGraph: RecipeGraph, width: number, height: number, recipeId: string = '') {
    this.width = width;
    this.height = height;
    this.rootNodeIds = [...recipeGraph.rootNodeIds];
    this.nodes = Object.values(recipeGraph.nodes).map(
      node => {
        const isMainRoot = recipeGraph.rootNodeIds.length > 0 && recipeGraph.rootNodeIds[0] === node.id;
        const dnode: DisplayNode = {
          id: node.id,
          name: resolveNodeTitle(node),
          type: getNodeType(node),
          data: node,
          recipeId: recipeId,
          isMainRoot: isMainRoot,
          leafDistance: 0,  // Temporary - will be computed later
          rootDistance: 0,  // Temporary - will be computed later
          // Initialize position from displayData if available
          x: node.displayData?.position?.x,
          y: node.displayData?.position?.y
        };
        return dnode
      }
    );
    this.nodesById = new Map(this.nodes.map((node) => [node.id, node]));

    this.childrenMap = new Map<string, DisplayNode[]>();
    this.descendantsMap = new Map<string, DisplayNode[]>();
    this.parentsMap = new Map<string, DisplayNode[]>();
    this.ancestorsMap = new Map<string, DisplayNode[]>();

    // Build all relationship maps
    this.buildChildrenMap();
    this.buildParentsMap();
    this.buildDescendantsMap();
    this.buildAncestorsMap();
    this.buildLinks();

    // Initialize graph processing
    this.computeNodeLevels();
    this.assignInitialPositions();
    this.assignLinkShapes();

    // Apply auto-fold for nodes marked as folded by default
    this.applyAutoFold();
  }

  /**
   * Set store callbacks for hidden state management.
   * Called by GraphVisualizer after creating the DisplayGraph.
   */
  setStoreCallbacks(callbacks: {
    hideNode: (nodeId: string) => void;
    showNode: (nodeId: string) => void;
    isNodeHidden: (nodeId: string) => boolean;
    isNodeExpanded?: (nodeId: string) => boolean;
    collapseNode?: (nodeId: string) => void;
  }): void {
    this.hideNodeFn = callbacks.hideNode;
    this.showNodeFn = callbacks.showNode;
    this.isNodeHiddenFn = callbacks.isNodeHidden;
    this.isNodeExpandedFn = callbacks.isNodeExpanded;
    this.collapseNodeFn = callbacks.collapseNode;
  }

  // === Private Map Building Methods ===

  private buildChildrenMap(): void {
    this.childrenMap = new Map(
      Array.from(this.nodesById.entries()).map(([id, dnode]) => {
        const children = dnode.data.children
          ?.map((childId: string) => this.nodesById.get(childId)!)
          .filter((child: any): child is DisplayNode => child !== undefined) || [];
        return [id, children];
      }));
  }

  private buildParentsMap(): void {
    // Build parentsMap (reverse of childrenMap)
    this.childrenMap.forEach((children, nodeId) => {
      children.forEach(child => {
        if (!this.parentsMap.has(child.id)) {
          this.parentsMap.set(child.id, []);
        }
        this.parentsMap.get(child.id)!.push(this.nodesById.get(nodeId)!);
      });
    });
  }

  private buildDescendantsMap(): void {
    // Build descendantsMap (all descendants, not just direct children)
    this.childrenMap.forEach((children, parentId) => {
      const descendants: DisplayNode[] = [];
      
      // Use a queue-based BFS approach to find all descendants
      const queue: DisplayNode[] = [...children];
      const visited = new Set<string>();
      
      while (queue.length > 0) {
        const current = queue.shift()!;
        
        if (!visited.has(current.id)) {
          visited.add(current.id);
          descendants.push(current);
          
          // Add children of current node to queue
          const currentChildren = this.childrenMap.get(current.id) || [];
          queue.push(...currentChildren);
        }
      }
      
      this.descendantsMap.set(parentId, descendants);
    });
  }

  private buildAncestorsMap(): void {
    // Build ascendantsMap (all ascendants, not just direct parents)
    this.parentsMap.forEach((parents, childId) => {
      const ascendants: DisplayNode[] = [];
      
      // Use a queue-based BFS approach to find all ascendants
      const queue: DisplayNode[] = [...parents];
      const visited = new Set<string>();
      
      while (queue.length > 0) {
        const current = queue.shift()!;
        
        if (!visited.has(current.id)) {
          visited.add(current.id);
          ascendants.push(current);
          
          // Add parents of current node to queue
          const currentParents = this.parentsMap.get(current.id) || [];
          queue.push(...currentParents);
        }
      }
      
      this.ancestorsMap.set(childId, ascendants);
    });
  }

  private buildLinks(): void {
    // Populate links
    this.childrenMap.forEach((children, parentId) => {
      children.forEach(childNode => {
        this.links.push({
          source: this.nodesById.get(parentId)!,
          target: childNode,
          shape: {x: 0, y: 0},  // Temporary - will be computed later
          active: true,
        });
      })
    });
  }

  // === Private Graph Processing Methods ===

  private computeNodeLevels(): void {
    // Find leaf nodes (nodes with no children) and root node (node with no parents)
    const leafNodes = this.nodes.filter(node => this.getChildren(node).length === 0);
    const rootNodes = this.nodes.filter(node => this.getParents(node).length === 0);

    // Initialize all nodes to distance 0
    this.nodes.forEach(node => {
      node.leafDistance = 0;
      node.rootDistance = 0;
    });

    // Calculate leafDistance (distance from leaves up)
    const leafQueue: DisplayNode[] = [];
    leafNodes.forEach(node => leafQueue.push(node));

    while (leafQueue.length > 0) {
      const currentNode = leafQueue.shift()!;

      // Find all parents of current node using graph method
      const parents = this.getParents(currentNode);

      // Update parent leafDistance
      parents.forEach(parentNode => {
        const newDistance = currentNode.leafDistance + 1;
        
        if (newDistance > parentNode.leafDistance) {
          parentNode.leafDistance = newDistance;
          leafQueue.push(parentNode);
        }
      });
    }

    // Calculate rootDistance (distance from root down)
    const rootQueue: DisplayNode[] = [];
    rootNodes.forEach(node => rootQueue.push(node));

    while (rootQueue.length > 0) {
      const currentNode = rootQueue.shift()!;

      // Find all children of current node using graph method
      const children = this.getChildren(currentNode);

      // Update children rootDistance
      children.forEach(childNode => {
        const newDistance = currentNode.rootDistance + 1;
        
        if (newDistance > childNode.rootDistance) {
          childNode.rootDistance = newDistance;
          rootQueue.push(childNode);
        }
      });
    }
  }

  private getRootDistanceY(rootDistance: number, levelHeight: number): number {
    // Normal system: rootDistance 0 (recipe) at top, higher distances below
    // Start from top with margin, go down for each level
    return 100 + (rootDistance * levelHeight);
  }

  private assignInitialPositions(): void {
    // Group nodes by leafDistance for horizontal positioning
    const nodesByLeafDistance = new Map<number, DisplayNode[]>();
    this.nodes.forEach(node => {
      // Skip nodes that already have positions from displayData
      if (node.x !== undefined && node.y !== undefined) {
        return;  // Keep loaded positions
      }

      if (!nodesByLeafDistance.has(node.leafDistance)) {
        nodesByLeafDistance.set(node.leafDistance, []);
      }
      nodesByLeafDistance.get(node.leafDistance)!.push(node);
    });

    // Calculate vertical spacing
    const levelHeight = LEVEL_HEIGHT;

    // Process levels from bottom (leaves) to top for horizontal positioning (same as original)
    const sortedLevels = Array.from(nodesByLeafDistance.keys()).sort((a, b) => a - b);
    
    sortedLevels.forEach(leafDistance => {
      const levelNodes = nodesByLeafDistance.get(leafDistance)!;

      if (leafDistance === 0) {
        // Leaf nodes - distribute evenly with proper spacing
        const nodeDiameter = NODE_RADIUS * 2;
        const spacing = NODE_SPACING;
        const totalWidth = levelNodes.length * nodeDiameter + (levelNodes.length - 1) * spacing;
        const startX = (this.width - totalWidth) / 2 + nodeDiameter / 2;

        levelNodes.forEach((node, index) => {
          const levelY = this.getRootDistanceY(node.rootDistance, levelHeight);
          node.x = startX + index * (nodeDiameter + spacing);
          node.y = levelY;
        });
      } else {
        // Parent nodes - center over children (same as original)
        levelNodes.forEach(node => {
          // Find children of this node using graph method
          const children = this.getChildren(node);
          
          if (children.length > 0) {
            // Calculate center position based on children
            const childrenX = children.map(child => child.x!).filter(x => x !== undefined);
            if (childrenX.length > 0) {
              const minX = Math.min(...childrenX);
              const maxX = Math.max(...childrenX);
              node.x = (minX + maxX) / 2;
            } else {
              // Fallback if children don't have positions yet
              node.x = this.width / 2;
            }
          } else {
            // No children - use default position
            node.x = this.width / 2;
          }

          const levelY = this.getRootDistanceY(node.rootDistance, levelHeight);
          node.y = levelY;
        });
      }
    });
  }

  private assignLinkShapes(): void {
    this.links.forEach(link => assignLinkShape(link));
  }

  // === Public Methods ===

  public updateLinkShape(link: Link<DisplayNode>): void {
    assignLinkShape(link);
  }

  public addLink(sourceId: string, targetId: string): Link<DisplayNode> | null {
    // Check if link already exists
    if (this.linkExists(sourceId, targetId)) {
      return null;
    }

    const sourceNode = this.nodesById.get(sourceId);
    const targetNode = this.nodesById.get(targetId);

    if (!sourceNode || !targetNode) {
      return null;
    }

    // Add target as child of source
    sourceNode.data.children.push(targetNode.data.id);

    // Add link to links array
    const newLink: Link<DisplayNode> = {
      source: sourceNode,
      target: targetNode,
      shape: {x: 0, y: 0},  // Will be computed later
      active: true,
    };
    this.links.push(newLink);

    // Rebuild ALL relationship maps to reflect new link
    this.rebuildRelationshipMaps();

    // Recompute positions and shapes
    this.computeNodeLevels();
    this.assignLinkShapes();

    return newLink;
  }

  /**
   * Calculate position for new node in lower-right quadrant with jitter
   */
  private positionNewNode(): {x: number, y: number} {
    // Lower-right quadrant: 75-95% width, 60-90% height
    const xRange = this.width * 0.2;   // 20% of width for jitter
    const yRange = this.height * 0.3;   // 30% of height for jitter

    const baseX = this.width * 0.75;   // Start at 75% width
    const baseY = this.height * 0.6;    // Start at 60% height

    // Add random jitter
    const jitterX = Math.random() * xRange;
    const jitterY = Math.random() * yRange;

    return {
      x: baseX + jitterX,
      y: baseY + jitterY
    };
  }

  /**
   * Add a new node to the graph
   * @param recipeNode The recipe node data to add
   * @param x Optional initial X position
   * @param y Optional initial Y position
   * @returns The created DisplayNode
   */
  public addNode(recipeNode: RecipeNode, x?: number, y?: number, recipeId?: string): DisplayNode {
    const {x: initialX, y: initialY} = (x !== undefined && y !== undefined) 
      ? {x, y} 
      : this.positionNewNode();
    // Create DisplayNode using same pattern as constructor
    const dnode: DisplayNode = {
      id: recipeNode.id,
      name: resolveNodeTitle(recipeNode),
      type: this.getNodeType(recipeNode),
      data: recipeNode,
      recipeId: recipeId || '',
      isMainRoot: false,
      leafDistance: 0,
      rootDistance: 0,
      x: initialX,
      y: initialY,
      fx: initialX,  // Fix initial position temporarily
      fy: initialY,  // Fix initial position temporarily
      vx: 0,
      vy: 0
    };

    // Add to arrays and maps
    this.nodes.push(dnode);
    this.nodesById.set(dnode.id, dnode);

    // Rebuild relationship maps and recompute levels
    this.rebuildRelationshipMaps();
    this.computeNodeLevels();
    this.assignLinkShapes();

    return dnode;
  }

  /**
   * Helper method to determine node type (same as getNodeType in constructor)
   */
  private getNodeType(node: RecipeNode): 'ingredient' | 'transformation' | 'combined' {
    const hasIngredient = !!node.ingredientInfo;
    const hasTransformation = !!node.transformationInfos?.length;

    if (hasIngredient && hasTransformation) return 'combined';
    if (hasIngredient) return 'ingredient';
    if (hasTransformation) return 'transformation';
    return 'ingredient'; // Default fallback for empty nodes
  }

  public removeLink(sourceId: string, targetId: string): Link<DisplayNode> | null {
    const linkIndex = this.links.findIndex(link => 
      link.source.id === sourceId && link.target.id === targetId
    );
    
    if (linkIndex === -1) {
      return null;
    }
    
    const removedLink = this.links.splice(linkIndex, 1)[0];
    
    // Remove target from source's children
    const sourceNode = this.nodesById.get(sourceId);
    if (sourceNode) {
      sourceNode.data.children = sourceNode.data.children.filter(
        childId => childId !== targetId
      );
    }
    
    // Rebuild ALL relationship maps to reflect deleted link
    this.rebuildRelationshipMaps();
    
    // Recompute positions and shapes
    this.computeNodeLevels();
    this.assignLinkShapes();
    
    return removedLink;
  }

  private rebuildRelationshipMaps(): void {
    // Clear and rebuild all relationship maps based on current links
    this.childrenMap.clear();
    this.parentsMap.clear();
    this.descendantsMap.clear();
    this.ancestorsMap.clear();

    this.buildChildrenMap();
    this.buildParentsMap();
    this.buildDescendantsMap();
    this.buildAncestorsMap();
  }

  private linkExists(sourceId: string, targetId: string): boolean {
    return this.links.some(link => 
      link.source.id === sourceId && link.target.id === targetId
    );
  }

  get root(): DisplayNode | undefined {
    return this.rootNodeIds.length > 0 ? this.nodesById.get(this.rootNodeIds[0]) : undefined
  }

  getChildren(node: DisplayNode): DisplayNode[] {
    return this.childrenMap.get(node.id) || []
  }

  getDescendants(node: DisplayNode): DisplayNode[] {
    return this.descendantsMap.get(node.id) || []
  }

  getParents(node: DisplayNode): DisplayNode[] {
    return this.parentsMap.get(node.id) || []
  }

  getAncestors(node: DisplayNode): DisplayNode[] {
    return this.ancestorsMap.get(node.id) || []
  }

  /**
   * Remove a node from the graph and all associated links
   * Cannot remove the root node to maintain graph validity
   * @param nodeId The ID of the node to remove
   * @returns The removed node or null if not found or if root
   */
  public removeNode(nodeId: string): DisplayNode | null {
    // Check if node exists
    const nodeToRemove = this.nodesById.get(nodeId);
    if (!nodeToRemove) {
      return null;
    }

    // Remove from rootNodeIds if it was a root
    this.rootNodeIds = this.rootNodeIds.filter(id => id !== nodeId);

    // Remove all associated links (both parent and child)
    for (let i = this.links.length - 1; i >= 0; i--) {
      const link = this.links[i];
      if (link.source.id === nodeId || link.target.id === nodeId) {
        this.links.splice(i, 1);
      }
    }

    // Remove node from parent's children arrays
    this.nodes.forEach(node => {
      node.data.children = node.data.children.filter(childId => childId !== nodeId);
    });

    // Remove node from arrays and maps
    const nodeIndex = this.nodes.findIndex(node => node.id == nodeId);
    if (nodeIndex !== -1) {
      this.nodes.splice(nodeIndex, 1);
    }
    this.nodesById.delete(nodeId);

    // Rebuild relationship maps to maintain consistency
    this.rebuildRelationshipMaps();

    // Recompute positions and shapes
    this.computeNodeLevels();
    this.assignLinkShapes();

    return nodeToRemove;
  }

  hideChildren(node: DisplayNode, checkParents: boolean = false) {
    // No store callbacks wired (e.g. a plain data snapshot built by
    // GraphVisualizer.buildRecipeGraph() for the ChangeTracker, never
    // rendered) — nothing to hide, and calling hideNodeFn would throw.
    if (!this.hideNodeFn) return;
    const directChildren = this.getChildren(node);
    directChildren.forEach(child => {
      // If checkParents (when hiding grandchildren), only hide if all parents are hidden
      const childParents = this.getParents(child);
      if (!checkParents || childParents.every(childParent => this.isNodeHiddenFn?.(childParent.id) ?? false)) {
        this.hideNodeFn!(child.id);
        this.hideChildren(child, true);
      }
    });
  }

  unhideChildren(node: DisplayNode) {
    // See hideChildren() — no-op without wired callbacks.
    if (!this.showNodeFn) return;
    const directChildren = this.getChildren(node);
    directChildren.forEach(child => {
      this.showNodeFn!(child.id);
      // Only unhide grandchildren if this child is not folded
      if (this.isNodeExpandedFn ? this.isNodeExpandedFn(child.id) : true) {
        this.unhideChildren(child);
      }
    });
  }

  /**
   * Apply auto-fold for nodes marked as folded by default.
   * Iterates all nodes and folds those with autoFold=true,
   * which marks their children as hidden.
   */
  applyAutoFold(): void {
    this.nodes.forEach(node => {
      if (node.data.autoFold) {
        if (this.collapseNodeFn) {
          this.collapseNodeFn(node.id);
        }
        this.hideChildren(node);
      }
    });
  }

  /**
   * Restore the graph to a previous state by mutating in place
   * This preserves object identity so all closures and references remain valid
   *
   * @param sourceGraph - The graph state to restore from
   */
  public restoreFromGraph(sourceGraph: DisplayGraph): void {
    // 1. Clear current state
    this.nodes.length = 0;
    this.links.length = 0;
    this.nodesById.clear();

    // 2. Copy rootNodeIds
    this.rootNodeIds = [...sourceGraph.rootNodeIds];

    // 3. Deep copy nodes from source
    // CRITICAL: We need to create NEW DisplayNode instances, not reuse source's
    // Otherwise we'd just be moving references around
    sourceGraph.nodes.forEach(sourceNode => {
      // Clone the RecipeNode data
      const clonedData = cloneNode(sourceNode.data);

      // Create new DisplayNode with cloned data
      const newNode: DisplayNode = {
        id: sourceNode.id,
        name: sourceNode.name,
        type: sourceNode.type,
        data: clonedData,
        recipeId: sourceNode.recipeId,
        isMainRoot: sourceNode.isMainRoot,
        leafDistance: sourceNode.leafDistance,
        rootDistance: sourceNode.rootDistance,
        // Copy position if exists
        x: sourceNode.x ?? 0,
        y: sourceNode.y ?? 0,
        vx: sourceNode.vx ?? 0,
        vy: sourceNode.vy ?? 0,
      };

      this.nodes.push(newNode);
      this.nodesById.set(newNode.id, newNode);
    });

    // 4. Rebuild all relationship maps from scratch
    // This ensures links point to the NEW DisplayNode instances
    this.rebuildRelationshipMaps();

    // 5. Rebuild links array
    // Links are created by buildLinks() which is called by rebuildRelationshipMaps()
    // First clear it, then rebuild
    this.links.length = 0;
    this.buildLinks();

    // 6. Recompute graph properties
    this.computeNodeLevels();
    this.assignLinkShapes();

    // 7. Re-apply auto-fold for nodes marked as folded by default
    this.applyAutoFold();
  }

  /**
   * Clone the display graph into a RecipeNode array for API updates
   * Converts DisplayNode[] back to RecipeNode[]
   */
  getNodesArray(): RecipeNode[] {
    return this.nodes.map(displayNode => displayNode.data);
  }

  /**
   * Import a subgraph (from ingredient recipe search) into this graph.
   *
   * The root node of the subgraph is the one that uses the searched ingredient.
   * Since the parent node already represents that ingredient, we skip importing
   * the root and instead graft its children directly under the parent.
   *
   * @param subgraphNodes - The nodes to import (keyed by their original IDs)
   * @param parentNodeId - The existing node to attach the imported children under
   * @returns The ID of the parent node (no new root is created)
   */
  public importSubgraph(
    subgraphNodes: Record<string, IngredientRecipeSubgraphNode>,
    parentNodeId: string,
  ): string {
    const origRootId = Object.keys(subgraphNodes)[0];

    // Collect all non-root nodes (skip the root which duplicates the ingredient)
    const nonRootIds = Object.keys(subgraphNodes).filter(id => id !== origRootId);
    if (nonRootIds.length === 0) {
      return parentNodeId;
    }

    // Generate new IDs for all non-root nodes
    const idMapping: Record<string, string> = {};
    for (const origId of nonRootIds) {
      idMapping[origId] = `imported_${Date.now()}_${origId}`;
    }
    // Also used for remapping transformation `parameters` (see
    // remapTransformationParams()) — a param referencing the skipped
    // root itself must become a reference to `parentNodeId`, since the
    // root's own children/transformations end up grafted onto it.
    const paramIdMapping: Record<string, string> = { ...idMapping, [origRootId]: parentNodeId };

    // ── Phase 1: Create all nodes with remapped children ──
    // We collect them first, then add all at once to batch the relationship rebuilds.
    const nodesToAdd: { newNode: RecipeNode; x: number; y: number }[] = [];

    for (const origId of nonRootIds) {
      const origNode = subgraphNodes[origId];
      const newId = idMapping[origId];

      // Remap children: skip references to the root (its children are being
      // attached to parent), map other children to their new IDs
      const mappedChildren = origNode.children
        .filter(cid => cid !== origRootId)
        .map(cid => idMapping[cid] ?? cid);

      const newNode: RecipeNode = {
        id: newId,
        name: origNode.name,
        description: origNode.description,
        imageUrl: origNode.imageUrl,
        autoFold: false,
        ingredientInfo: origNode.ingredientInfo ? {
          ingredient: { ...origNode.ingredientInfo.ingredient },
          description: origNode.ingredientInfo.description,
          quantity: origNode.ingredientInfo.quantity ? { ...origNode.ingredientInfo.quantity } : undefined,
        } : undefined,
        transformationInfos: (origNode.transformationInfos ?? []).map(ti => ({
          transformation: ti.transformation ? { ...ti.transformation } : undefined,
          description: ti.description,
          parameters: remapTransformationParams(ti.parameters, paramIdMapping),
          duration: ti.duration,
          attentionDuration: ti.attentionDuration,
        })),
        toolInfos: origNode.toolInfos?.map(ti => ({
          tool: { ...ti.tool },
          description: ti.description,
          fresh: ti.fresh,
          hidden: ti.hidden,
          counts: ti.counts,
        })),
        children: mappedChildren,
        stepOrder: origNode.stepOrder,
      };

      const pNode = this.nodesById.get(parentNodeId);
      const baseX = pNode ? (pNode.x ?? this.width / 2) : this.width / 2;
      const baseY = pNode ? (pNode.y ?? this.height * 0.3) : this.height * 0.3;

      const idx = nonRootIds.indexOf(origId);
      const x = baseX + 80 + (idx % 3) * 30;
      const y = baseY + 100 + Math.floor(idx / 3) * 60;

      nodesToAdd.push({ newNode, x, y });
    }

    // ── Phase 2: Add all nodes to the graph ──
    for (const { newNode, x, y } of nodesToAdd) {
      this.addNode(newNode, x, y);
    }

    // ── Phase 3: Create links for ALL child relationships ──
    // addNode() only rebuilds relationship maps but does NOT populate the
    // links[] array. We must explicitly create every edge so D3 can render them.
    for (const origId of nonRootIds) {
      const origNode = subgraphNodes[origId];
      const sourceId = idMapping[origId];

      for (const childId of origNode.children) {
        if (childId === origRootId) continue;  // skip back-reference to root
        const targetId = idMapping[childId];
        if (targetId) {
          this.addLink(sourceId, targetId);
        }
      }
    }

    // Graft the skipped root's children directly under the parent
    const rootNode = subgraphNodes[origRootId];
    for (const childId of rootNode.children) {
      const newChildId = idMapping[childId];
      if (newChildId) {
        this.addLink(parentNodeId, newChildId);
      }
    }

    // ── Phase 4: Transfer the root's transformations/tools to the parent ──
    // If the skipped root had transformations (e.g. "mix" on a "dough" node)
    // or tools, merge them onto the parent node so they aren't lost.
    const parentNode = this.nodesById.get(parentNodeId);
    if (parentNode && rootNode.transformationInfos?.length && !parentNode.data.transformationInfos?.length) {
      parentNode.data.transformationInfos = rootNode.transformationInfos.map(ti => ({
        transformation: ti.transformation ? { ...ti.transformation } : undefined,
        description: ti.description,
        parameters: remapTransformationParams(ti.parameters, paramIdMapping),
        duration: ti.duration,
        attentionDuration: ti.attentionDuration,
      }));
      parentNode.name = resolveNodeTitle(parentNode.data);
      parentNode.type = getNodeType(parentNode.data);
    }
    if (parentNode && rootNode.toolInfos?.length && !parentNode.data.toolInfos?.length) {
      parentNode.data.toolInfos = rootNode.toolInfos.map(ti => ({
        tool: { ...ti.tool },
        description: ti.description,
        fresh: ti.fresh,
        hidden: ti.hidden,
        counts: ti.counts,
      }));
    }

    return parentNodeId;
  }

  /**
   * Add a subgraph of nodes to this graph, creating DisplayNodes for all
   * returned nodes and establishing tree-layout positions under the given root.
   *
   * Used by GraphVisualizer when expanding a node whose subgraph is being
   * fetched from the API. If a node with the same ID already exists in the
   * graph, it is skipped (no duplicate creation).
   *
   * @param rootNodeId - The ID of the root node under which to place the subgraph
   * @param subgraphNodes - All nodes in the subgraph (including the root), keyed by ID
   * @returns Array of newly added DisplayNode IDs
   */
  public addSubgraph(
    rootNodeId: string,
    subgraphNodes: Record<string, RecipeNode>,
  ): string[] {
    const addedIds: string[] = [];

    // Get the root DisplayNode for position reference
    const rootDNode = this.nodesById.get(rootNodeId);
    if (!rootDNode) return addedIds;

    const rootApiNode = subgraphNodes[rootNodeId];
    if (!rootApiNode) return addedIds;

    // ── Phase 1: Create DisplayNodes for all non-root nodes ──
    for (const [nid, apiNode] of Object.entries(subgraphNodes)) {
      if (nid === rootNodeId) continue;
      if (this.nodesById.has(nid)) continue; // Already exists

      const dnode: DisplayNode = {
        id: apiNode.id,
        name: resolveNodeTitle(apiNode),
        type: getNodeType(apiNode),
        data: apiNode,
        recipeId: rootDNode.recipeId,
        isMainRoot: false,
        leafDistance: 0,
        rootDistance: 0,
        x: undefined,  // Will be assigned by tree layout
        y: undefined,
        vx: 0,
        vy: 0,
      };

      this.nodes.push(dnode);
      this.nodesById.set(dnode.id, dnode);
      addedIds.push(dnode.id);
    }

    // ── Phase 2: Rebuild relationship maps ──
    this.rebuildRelationshipMaps();

    // ── Phase 3: Compute node levels ──
    this.computeNodeLevels();

    // ── Phase 4: Assign tree-layout positions ──
    // Position new nodes below the root, spread by depth
    if (addedIds.length > 0) {
      const parentX = rootDNode.x ?? this.width / 2;
      const parentY = rootDNode.y ?? 100;
      const levelHeight = 120;
      const nodeDiameter = NODE_RADIUS * 2;
      const spacing = NODE_SPACING;

      // Group new nodes by rootDistance
      const groups = new Map<number, DisplayNode[]>();
      for (const id of addedIds) {
        const dnode = this.nodesById.get(id);
        if (dnode) {
          const dist = dnode.rootDistance;
          if (!groups.has(dist)) groups.set(dist, []);
          groups.get(dist)!.push(dnode);
        }
      }

      const sortedLevels = Array.from(groups.keys()).sort((a, b) => a - b);

      for (const distance of sortedLevels) {
        const levelNodes = groups.get(distance)!;
        const y = parentY + (distance + 1) * levelHeight;
        const totalWidth = levelNodes.length * nodeDiameter + (levelNodes.length - 1) * spacing;
        const startX = parentX - totalWidth / 2 + nodeDiameter / 2;

        levelNodes.forEach((dnode, index) => {
          dnode.x = startX + index * (nodeDiameter + spacing);
          dnode.y = y;
        });
      }
    }

    // ── Phase 5: Create links ──
    // Build links from children relationships in API data
    for (const [nid, apiNode] of Object.entries(subgraphNodes)) {
      if (nid === rootNodeId) continue;
      const sourceNode = this.nodesById.get(nid);
      if (!sourceNode) continue;

      for (const childId of apiNode.children) {
        const targetNode = this.nodesById.get(childId);
        if (!targetNode) continue;
        if (this.linkExists(nid, childId)) continue;

        const newLink: Link<DisplayNode> = {
          source: sourceNode,
          target: targetNode,
          shape: { x: 0, y: 0 },
          active: true,
        };
        this.links.push(newLink);
      }
    }

    // Create links from root to its children from API data
    for (const childId of rootApiNode.children) {
      const targetNode = this.nodesById.get(childId);
      if (!targetNode) continue;
      if (this.linkExists(rootNodeId, childId)) continue;

      const newLink: Link<DisplayNode> = {
        source: rootDNode,
        target: targetNode,
        shape: { x: 0, y: 0 },
        active: true,
      };
      this.links.push(newLink);
    }

    // ── Phase 6: Assign link shapes ──
    this.assignLinkShapes();

    return addedIds;
  }

  /**
   * Create a deep clone of the current graph state
   * Used for change tracking and restoration
   */
  clone(): DisplayGraph {
    // Create a deep copy of the RecipeGraph
    const recipeGraphClone: RecipeGraph = {
      rootNodeIds: [...this.rootNodeIds],
      nodes: {}
    };

    // Deep copy all nodes with displayData
    this.nodes.forEach(node => {
      const clonedNode = cloneNode(node.data);

      // Preserve recipeId on cloned data
      clonedNode.recipeId = node.recipeId;

      // Add current positions to displayData
      if (node.x !== undefined && node.y !== undefined) {
        clonedNode.displayData = {
          position: {
            x: node.x,
            y: node.y
          }
        };
      }

      recipeGraphClone.nodes[node.id] = clonedNode;
    });

    // Get recipeId from the first node (all nodes in a DisplayGraph share one)
    const recipeId = this.nodes.length > 0 ? this.nodes[0].recipeId : '';

    // Create new DisplayGraph instance (will load positions from displayData)
    const clonedGraph = new DisplayGraph(recipeGraphClone, this.width, this.height, recipeId);

    // CRITICAL: Fold state is managed by RecipeStore
    // (hidden/selected/hovered state is also managed by RecipeStore)
    return clonedGraph;
  }
}

function getNodeType(node: RecipeNode): 'ingredient' | 'transformation' | 'combined' {
  const hasIngredient = !!node.ingredientInfo;
  const hasTransformation = !!node.transformationInfos?.length;

  if (hasIngredient && hasTransformation) return 'combined';
  if (hasIngredient) return 'ingredient';
  if (hasTransformation) return 'transformation';
  return 'ingredient';
}

/**
 * Remap a transformation usage's `parameters` through `idMapping` —
 * every `params.X` a render template references is a node id, entered
 * via the Transformació tab's "pick node" button (see NodeVisualizer.ts's
 * renderTransformationParametersSection()), so cloning the raw
 * `parameters` object verbatim (the way every other field here already
 * correctly does) leaves it pointing at whatever id the referenced node
 * had BEFORE a subgraph import/graft gave it a new one — the render
 * template's `only(self.children, params.principal)`-style lookups then
 * silently fail to find it. A value with no entry in `idMapping` is left
 * untouched: it's either a reference to a node outside what's being
 * remapped, or (parameters have no declared type) a genuine non-node-id
 * value that was simply typed in rather than picked.
 */
export function remapTransformationParams(
  parameters: Record<string, unknown> | undefined,
  idMapping: Record<string, string>,
): Record<string, unknown> | undefined {
  if (!parameters) return undefined;
  const remapped: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(parameters)) {
    remapped[key] = typeof value === 'string' && Object.prototype.hasOwnProperty.call(idMapping, value)
      ? idMapping[value]
      : value;
  }
  return remapped;
}

export function cloneNode(node: RecipeNode): RecipeNode {
  return {
    id: node.id,
    name: node.name,
    description: node.description,
    imageUrl: node.imageUrl,
    autoFold: node.autoFold,
    hidden: node.hidden,
    recipeId: node.recipeId,
    ingredientInfo: node.ingredientInfo ? {
      ingredient: node.ingredientInfo.ingredient ? { ...node.ingredientInfo.ingredient } : undefined as any,
      description: node.ingredientInfo.description,
      quantity: node.ingredientInfo.quantity ? { ...node.ingredientInfo.quantity } : undefined
    } : undefined,
    transformationInfos: node.transformationInfos?.map(ti => ({
      transformation: ti.transformation ? { ...ti.transformation } : undefined,
      description: ti.description,
      parameters: ti.parameters ? { ...ti.parameters } : undefined,
      duration: ti.duration,
      attentionDuration: ti.attentionDuration,
    })),
    toolInfos: node.toolInfos?.map(ti => ({
      tool: { ...ti.tool },
      description: ti.description,
      fresh: ti.fresh,
      hidden: ti.hidden,
      counts: ti.counts,
    })),
    children: [...node.children],
    stepOrder: node.stepOrder,
  }
}

/**
 * Resolve the display title for a node.
 * Priority: node name → ingredient name → tool name(s) (for a bare tool
 * usage with no transformation of its own) → transformation description
 * → transformation name → fallback.
 * Transformation-derived names are wrapped in brackets to signal they are action nodes, not products.
 */
export function resolveNodeTitle(node: RecipeNode): string {
  if (node.name) return node.name;
  if (node.ingredientInfo?.ingredient?.name) return node.ingredientInfo.ingredient.name;
  const hasTransformation = node.transformationInfos?.some(ti => ti.transformation);
  if (!hasTransformation && node.toolInfos?.length) {
    return node.toolInfos.map(ti => ti.tool.name).join(' i ');
  }
  // Transformation-derived name → bracketed to indicate it's an action, not a
  // product. Several transformations in order join into one label, e.g.
  // "[Talla i Pela]".
  const labels = (node.transformationInfos ?? [])
    .map(ti => ti.description || ti.transformation?.name)
    .filter((label): label is string => !!label);
  if (labels.length > 0) return `[${labels.join(' i ')}]`;
  return 'Unnamed Node';
}
